<template>
  <div class="result-view-layout" :class="{ 'dragging': isDragging }">
    <!-- 左侧信息面板 -->
    <div class="left-info-panel" :style="{ width: leftPanelWidth + 'px' }">
      <div class="info-header">
        <h3>故事梗概与人物小传</h3>
      </div>
      <div class="info-content">
        <!-- 故事梗概与人物小传页面显示剧本信息 -->
        <template v-if="activeSectionId === 'synopsis'">
          <div class="info-item">
            <label>项目名称：</label>
            <span>{{ scriptInfo.title || '未命名' }}</span>
          </div>
          <div class="info-item">
            <label>核心受众：</label>
            <span>{{ scriptInfo.core_audience || '未设置' }}</span>
          </div>
          <div class="info-item">
            <label>创作手法：</label>
            <span>{{ scriptInfo.writing_techniques || '未设置' }}</span>
          </div>
          <div class="info-item">
            <label>题材：</label>
            <span>{{ scriptInfo.theme || '未设置' }}</span>
          </div>
          <div class="info-item description">
            <label>剧本描述：</label>
            <div class="description-text">{{ scriptInfo.script_description || '暂无描述' }}</div>
          </div>
        </template>

        <template v-else>
          <div class="synopsis-content">
            <div v-if="baseText" class="result-text synopsis-preview-text" v-html="baseText"></div>
            <div v-else class="no-messages">暂无故事梗概与人物小传内容</div>
          </div>
        </template>
      </div>
      <!-- 拖拽手柄 -->
      <div class="resize-handle" @mousedown="handleMouseDown" :class="{ 'dragging': isDragging }">
        <span class="resize-icon">&lt;&gt;</span>
      </div>
    </div>

    <div class="result-main-content">
      <!-- 固定顶部区域 -->
      <div class="fixed-header">
        <div class="header-row">
          <el-button class="back-btn" type="default" :icon="ArrowLeft" @click="handleBack">返回</el-button>

          <!-- 详细剧本页面的集数选择器和生成按钮 -->
          <div v-if="activeSectionId === 'script'" class="script-controls">
            <div class="episode-range-selector">
              <label>生成集数范围：</label>
              <el-select v-model="batchStartEpisode" placeholder="起始" size="small" class="episode-select">
                <el-option v-for="episode in episodeOptions" :key="episode" :label="`第${episode}集`" :value="episode" />
              </el-select>
              <span class="range-separator">至</span>
              <el-select v-model="batchEndEpisode" placeholder="结束" size="small" class="episode-select">
                <el-option v-for="episode in episodeOptions" :key="episode" :label="`第${episode}集`" :value="episode" />
              </el-select>
            </div>
            <el-button type="primary" size="small" @click="handleBatchGenerate"
              :disabled="batchGenerating || generatingScriptEpisodes.length > 0 || !batchStartEpisode || !batchEndEpisode">
              {{ batchGenerating ? '生成中...' : '生成' }}
            </el-button>
          </div>

          <!-- 进度条或标题 -->
          <div v-if="showOutlineProgress" class="fixed-progress-container">
            <span class="progress-title">{{ progressTitle }}</span>
            <span class="progress-text">{{ progressText }}</span>
            <el-progress :percentage="progressPercentage" :status="progressStatus" :stroke-width="6" :show-text="false"
              style="flex: 1; margin: 0 12px;" />
          </div>
          <div v-else class="result-title">{{ currentTitle }}</div>
          <el-button v-if="activeSectionId === 'synopsis'" class="save-btn" type="primary" @click="handleSave"
            :disabled="isGenerating">保存</el-button>
          <el-button v-if="activeSectionId === 'script'" class="export-btn" type="success"
            @click="handleExportScriptDocx" :disabled="!hasAnyScriptContent || isGenerating || scriptLoading">
            导出DOCX
          </el-button>
        </div>
      </div>

      <!-- 可滚动内容区域 -->
      <div class="scrollable-content" ref="mainContentRef" v-loading="loading" :element-loading-text="loadingText">
        <!-- 故事梗概与人物小传 -->
        <div id="synopsis" class="content-section" v-show="activeSectionId === 'synopsis'">
          <div v-if="baseText" class="result-text" contenteditable="true" v-html="baseText"></div>
          <div v-else class="generate-button-container">
            <button class="generate-button" @click="handleGenerateSynopsis"
              :disabled="isGenerating">开始生成故事梗概和人物小传</button>
          </div>
        </div>

        <!-- 分集大纲 -->
        <div id="outline" class="content-section" v-show="activeSectionId === 'outline'" v-loading="outlineLoading"
          element-loading-text="正在加载分集大纲数据...">

          <div class="outline-layout">
            <!-- 左侧区域：多个面板 -->
            <div class="outline-left-container">
              <!-- 添加面板按钮 -->
              <div class="add-panel-section">
                <el-button type="primary" @click="addOutlinePanel" icon="Plus" size="small" class="add-panel-btn">
                  添加段落
                </el-button>
              </div>

              <!-- 面板列表 -->
              <div class="outline-panels-list">
                <div v-for="(panel, index) in outlinePanels" :key="panel.id" class="outline-left-panel">
                  <!-- 面板头部 -->
                  <div class="panel-header">
                    <span class="panel-title">段落 {{ index + 1 }}</span>
                    <el-button type="danger" size="small" @click="removeOutlinePanel(panel.id)" icon="Delete" circle
                      v-if="outlinePanels.length > 1" />
                  </div>

                  <!-- 上部区域：文本输入框 -->
                  <div class="outline-input-area">
                    <el-input v-model="panel.inputText" type="textarea" :rows="8" placeholder="请简短描述分集大纲内容，后面由莎士比耶为你扩写"
                      resize="none" />
                  </div>

                  <!-- 下部区域：按钮和集数选择器 -->
                  <div class="outline-control-area">
                    <div class="episode-selector">
                      <label>集数范围：</label>
                      <div class="episode-range">
                        <el-select v-model="panel.startEpisode" placeholder="起始" size="small" class="episode-select">
                          <el-option v-for="episode in episodeOptions" :key="episode" :label="`第${episode}集`"
                            :value="episode" />
                        </el-select>
                        <span class="range-separator">至</span>
                        <el-select v-model="panel.endEpisode" placeholder="结束" size="small" class="episode-select">
                          <el-option v-for="episode in episodeOptions" :key="episode" :label="`第${episode}集`"
                            :value="episode" />
                        </el-select>
                      </div>
                    </div>
                    <div class="outline-buttons">
                      <!-- <el-button type="default" @click="handleBeautifyOutline(panel.id)" :disabled="isGenerating"
                        size="small">美化</el-button> -->
                      <!-- 只有当textarea有内容时才显示按钮 -->
                      <template v-if="hasInputText(panel.id)">
                        <!-- 根据条件显示不同的按钮组合 -->
                        <template v-if="shouldShowContinueGenerate(panel.id)">
                          <el-button type="success" @click="handleContinueGenerate(panel.id)" :disabled="isGenerating"
                            size="small">继续生成</el-button>
                          <el-button type="primary" @click="handleGenerateOutline(panel.id)" :disabled="isGenerating"
                            size="small">全部重新生成</el-button>
                        </template>
                        <template v-else>
                          <el-button type="primary" @click="handleGenerateOutline(panel.id)" :disabled="isGenerating"
                            size="small">生成</el-button>
                        </template>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 右侧区域：可编辑文本框组 -->
            <div class="outline-right-panel">
              <div class="episode-textboxes">
                <div v-for="episode in episodeOptions" :key="episode" class="episode-card">
                  <div class="episode-content">
                    <el-input v-model="outlineEpisodeTexts[episode]" type="textarea" :rows="12"
                      :placeholder="generatingEpisodes.includes(episode) ? 'Loading 加载中...' : savingEpisodes.includes(episode) ? 'Loading 保存中...' : `第${episode}集的详细大纲内容...`"
                      :disabled="generatingEpisodes.includes(episode) || savingEpisodes.includes(episode)"
                      :loading="generatingEpisodes.includes(episode) || savingEpisodes.includes(episode)" resize="none"
                      class="episode-textarea" />
                  </div>
                  <div class="episode-actions">
                    <div class="word-count">{{ getWordCount(outlineEpisodeTexts[episode]) }} 字</div>
                    <div class="action-buttons">
                      <el-button v-if="hasEpisodeContent(episode)" size="small" @click="clearEpisodeContent(episode)"
                        :disabled="generatingEpisodes.includes(episode) || savingEpisodes.includes(episode)">重新生成</el-button>
                      <el-button type="primary" size="small" @click="saveEpisodeContent(episode)"
                        :disabled="generatingEpisodes.includes(episode) || savingEpisodes.includes(episode)"
                        :loading="savingEpisodes.includes(episode)">
                        <i class="el-icon-check" v-if="!savingEpisodes.includes(episode)"></i>
                        {{ savingEpisodes.includes(episode) ? '保存中...' : '保存' }}
                      </el-button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 详细剧本 -->
        <div id="script" class="content-section" v-show="activeSectionId === 'script'">
          <!-- 加载状态提示 -->
          <div v-if="scriptLoading" class="loading-container" v-loading="scriptLoading"
            element-loading-text="正在加载详细剧本数据...">
            <div style="height: 400px;"></div>
          </div>
          <div class="script-layout" v-else>
            <!-- 左侧内容区域 -->
            <div class="script-left-container">
              <div class="script-panel">
                <div class="episode-textboxes">
                  <div v-for="episode in episodeOptions" :key="episode" class="episode-card">
                    <div class="episode-content">
                      <el-input v-model="outlineEpisodeTexts[episode]" type="textarea" :rows="15"
                        :placeholder="scriptLoading ? 'Loading 数据加载中...' : `第${episode}集的分集大纲内容...`"
                        :disabled="scriptLoading" :loading="scriptLoading" resize="none" class="episode-textarea" />
                    </div>
                    <div class="episode-actions">
                      <div class="word-count">{{ getWordCount(outlineEpisodeTexts[episode]) }} 字</div>
                      <div class="action-buttons">
                        <el-button type="primary" size="small" @click="generateScriptContent(episode)"
                          :disabled="generatingScriptEpisodes.includes(episode)"
                          :loading="generatingScriptEpisodes.includes(episode)">
                          <i class="el-icon-check" v-if="!generatingScriptEpisodes.includes(episode)"></i>
                          {{ generatingScriptEpisodes.includes(episode) ? '生成中...' : '生成' }}
                        </el-button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 右侧内容区域 -->
            <div class="script-right-container">
              <div class="script-panel">
                <div class="episode-textboxes">
                  <div v-for="episode in episodeOptions" :key="episode" class="episode-card">
                    <div class="episode-content">
                      <el-input v-model="scriptEpisodeTexts[episode]" type="textarea" :rows="15"
                        :placeholder="generatingScriptEpisodes.includes(episode) ? 'Loading 数据加载中...' : `第${episode}集的详细剧本内容...`"
                        :disabled="generatingScriptEpisodes.includes(episode) || savingEpisodes.includes(episode)"
                        :loading="generatingScriptEpisodes.includes(episode)" resize="none" class="episode-textarea" />
                    </div>
                    <div class="episode-actions">
                      <div class="word-count">{{ getWordCount(scriptEpisodeTexts[episode]) }} 字</div>
                      <div class="action-buttons">
                        <el-button v-if="hasScriptContent(episode)" size="small" @click="regenerateScriptContent(episode)"
                          :disabled="generatingScriptEpisodes.includes(episode) || savingEpisodes.includes(episode)">重新生成</el-button>
                        <el-button type="primary" size="small" @click="saveEpisodeContent(episode, 'script')"
                          :disabled="savingEpisodes.includes(episode) || generatingScriptEpisodes.includes(episode)"
                          :loading="savingEpisodes.includes(episode)">
                          <i class="el-icon-check" v-if="!savingEpisodes.includes(episode)"></i>
                          {{ savingEpisodes.includes(episode) ? '保存中...' : '保存' }}
                        </el-button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="bottom-gap"></div>
      </div>
    </div>
    <RightSidebarComponent :showSynopsis="true" :showCharacter="false" :showOutline="true" :showScript="true"
      :showTranscript="true" :active-id="activeSectionId" :disabled="isGenerating || storyBibleInitializing"
      :hasOutlineData="hasOutlineData" @navigate="handleNavigate" />

    <el-dialog v-model="regenerateDialogVisible" :title="regenerateDialogMode === 'script' ? '重新生成详细剧本' : '重新生成分集大纲'" width="600px" :close-on-click-modal="false"
      :close-on-press-escape="false">
      <div class="regenerate-dialog-content">
        <p class="dialog-description">
          重新生成第 {{ currentRegenerateEpisode }} 集的{{ regenerateDialogMode === 'script' ? '详细剧本' : '分集大纲' }}，请输入您的要求或描述：
        </p>
        <el-input v-model="regenerateInputText" type="textarea" :rows="8" placeholder="请输入您对重新生成内容的要求或描述（最多1000字）"
          maxlength="1000" show-word-limit resize="none" />
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="handleRegenerateCancel">取消</el-button>
          <el-button type="primary" @click="handleRegenerateConfirm">确认</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted, onActivated, computed, nextTick, watch } from 'vue';
import RightSidebarComponent from '../../components/RightSidebarComponent.vue';
import { useRoute, useRouter } from 'vue-router';
import { ArrowLeft, Plus, Close, Delete } from '@element-plus/icons-vue';
import { ElMessage, ElMessageBox, ElInput, ElButton, ElSelect, ElOption, ElAlert, ElNotification, ElProgress } from 'element-plus';
import request from '@/api/request';
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from 'docx';

export default {
  name: 'ResultView',
  components: { RightSidebarComponent },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const currentScriptId = ref(String(route.query.id || localStorage.getItem('currentScriptId') || ''));
    if (currentScriptId.value) {
      localStorage.setItem('currentScriptId', currentScriptId.value);
    }

    // 剧本信息
    const scriptInfo = ref({
      title: '',
      core_audience: '',
      writing_techniques: '',
      theme: '',
      script_description: ''
    });

    const baseText = ref(null);
    const rawBaseText = ref('');
    const outlineText = ref('');
    const loading = ref(false);
    const loadingText = ref('正在加载中');
    const outlineLoading = ref(false); // 分集大纲数据加载状态
    const isGenerating = ref(false); // 控制生成过程中的按钮禁用状态
    const currentGeneratingEpisode = ref(null); // 当前正在生成的集数
    const generatingEpisodes = ref([]); // 正在生成的集数范围
    const savingEpisodes = ref([]); // 正在保存的集数范围

    // 分集大纲相关变量
    const outlineInputText = ref(''); // 左侧输入框内容（保留兼容性）
    const selectedEpisode = ref(1); // 当前选中的集数（保留兼容性）
    const episodeTexts = ref({}); // 存储每集的文本内容

    // 面板管理
    const outlinePanels = ref([
      {
        id: 1,
        inputText: '',
        sylloge: '1-10',
        startEpisode: 1,
        endEpisode: 10,
        isSaved: false // 标识是否已保存到服务器
      }
    ]); // 多个面板数据
    const nextPanelId = ref(2); // 下一个面板ID


    const outlineEpisodeTexts = ref({}); // 存储每集的大纲文本内容

    // 详细剧本相关变量
    const scriptEpisodeTexts = ref({}); // 存储每集的详细剧本内容
    const generatingScriptEpisodes = ref([]); // 正在生成详细剧本的集数范围
    const scriptLoading = ref(false); // 详细剧本数据加载状态

    // 批量生成相关变量
    const batchStartEpisode = ref(1); // 批量生成起始集数
    const batchEndEpisode = ref(10); // 批量生成结束集数
    const batchGenerating = ref(false); // 批量生成状态

    // 故事走向初始化相关变量
    const storyBibleInitializing = ref(false); // 故事走向初始化状态

    // 重新生成对话框相关变量
    const regenerateDialogVisible = ref(false); // 对话框显示状态
    const regenerateInputText = ref(''); // 对话框输入框内容
    const currentRegenerateEpisode = ref(null); // 当前要重新生成的集数
    const regenerateDialogMode = ref('outline');

    // 进度条相关变量
    const totalEpisodesToGenerate = ref(0); // 总共需要生成的集数
    const completedEpisodes = ref(0); // 已完成生成的集数
    const currentGenerationType = ref(''); // 当前生成类型：'outline' 或 'regenerate'
    const currentGenerationStartEpisode = ref(1); // 当前生成的起始集数
    const currentGenerationEndEpisode = ref(1); // 当前生成的结束集数
    const currentGeneratingEpisodeNumber = ref(0); // 当前正在生成的集数
    const regenerateProgress = ref(0); // 重新生成时的特殊进度控制

    // 左侧面板拖拽调整宽度相关变量
    const leftPanelWidth = ref(280); // 左侧面板宽度，默认280px
    const isDragging = ref(false); // 是否正在拖拽

    // 聊天对话框相关变量
    const chatMessages = ref([]); // 聊天消息列表
    const chatInputText = ref(''); // 聊天输入框内容
    const dragStartX = ref(0); // 拖拽开始时的鼠标X坐标
    const dragStartWidth = ref(0); // 拖拽开始时的面板宽度

    // 集数选项（默认1-10集，可根据需要调整）
    const episodeOptions = computed(() => {
      const min = 1;
      const max = 100;
      const options = [];
      for (let i = min; i <= max; i++) {
        options.push(i);
      }
      return options;
    });

    // 计算属性：检测段落是否需要显示继续生成按钮
    const shouldShowContinueGenerate = computed(() => {
      return (panelId) => {
        const panel = outlinePanels.value.find(p => p.id === panelId);
        if (!panel || !panel.sylloge) return false;

        // 解析段落的集数范围
        const syllogeMatch = panel.sylloge.match(/^(\d+)-(\d+)$/);
        if (!syllogeMatch) return false;

        const startEpisode = parseInt(syllogeMatch[1]);
        const endEpisode = parseInt(syllogeMatch[2]);

        // 计算已生成的分集大纲数量
        let generatedCount = 0;
        for (let i = startEpisode; i <= endEpisode; i++) {
          if (outlineEpisodeTexts.value[i] && outlineEpisodeTexts.value[i].trim() &&
            outlineEpisodeTexts.value[i] !== 'AI生成中...') {
            generatedCount++;
          }
        }

        const totalCount = endEpisode - startEpisode + 1;
        // 如果已生成数量小于总数量，且已生成数量大于0，则显示继续生成按钮
        return generatedCount > 0 && generatedCount < totalCount;
      };
    });

    // 计算属性：获取下一个需要生成的集数
    const getNextEpisodeToGenerate = computed(() => {
      return (panelId) => {
        const panel = outlinePanels.value.find(p => p.id === panelId);
        if (!panel || !panel.sylloge) return null;

        const syllogeMatch = panel.sylloge.match(/^(\d+)-(\d+)$/);
        if (!syllogeMatch) return null;

        const startEpisode = parseInt(syllogeMatch[1]);
        const endEpisode = parseInt(syllogeMatch[2]);

        // 找到第一个未生成的集数
        for (let i = startEpisode; i <= endEpisode; i++) {
          if (!outlineEpisodeTexts.value[i] || !outlineEpisodeTexts.value[i].trim() ||
            outlineEpisodeTexts.value[i] === 'AI生成中...') {
            return i;
          }
        }

        return null;
      };
    });

    // 计算属性：获取已生成的集数数量
    const getGeneratedEpisodeCount = computed(() => {
      return (panelId) => {
        const panel = outlinePanels.value.find(p => p.id === panelId);
        if (!panel || !panel.sylloge) return 0;

        const syllogeMatch = panel.sylloge.match(/^(\d+)-(\d+)$/);
        if (!syllogeMatch) return 0;

        const startEpisode = parseInt(syllogeMatch[1]);
        const endEpisode = parseInt(syllogeMatch[2]);

        let count = 0;
        for (let i = startEpisode; i <= endEpisode; i++) {
          if (outlineEpisodeTexts.value[i] && outlineEpisodeTexts.value[i].trim() &&
            outlineEpisodeTexts.value[i] !== 'AI生成中...') {
            count++;
          }
        }

        return count;
      };
    });

    // 计算属性：检测段落textarea是否有内容
    const hasInputText = computed(() => {
      return (panelId) => {
        const panel = outlinePanels.value.find(p => p.id === panelId);
        return panel && panel.inputText && panel.inputText.trim().length > 0;
      };
    });

    // 计算属性：检测单集大纲内容是否有内容
    const hasEpisodeContent = computed(() => {
      return (episode) => {
        const content = outlineEpisodeTexts.value[episode];
        return content && content.trim().length > 0 && content !== 'AI生成中...';
      };
    });

    // 新增：检查指定集数是否有详细剧本内容
    const hasScriptContent = computed(() => {
      return (episode) => {
        const content = scriptEpisodeTexts.value[episode];
        return content && content.trim().length > 0 && content !== 'AI生成中...';
      };
    });

    const hasAnyScriptContent = computed(() => {
      return Object.values(scriptEpisodeTexts.value || {}).some((content) => {
        return typeof content === 'string' && content.trim() && content !== 'AI生成中...';
      });
    });

    // 初始化集数文本
    const initEpisodeTexts = () => {
      episodeOptions.value.forEach(episode => {
        if (!episodeTexts.value[episode]) {
          episodeTexts.value[episode] = '';
        }
        if (!outlineEpisodeTexts.value[episode]) {
          outlineEpisodeTexts.value[episode] = '';
        }
      });
    };

    // 标题映射
    const titleMap = {
      synopsis: '故事梗概与人物小传',
      outline: '分集大纲',
      script: '详细剧本'
    };

    // 当前活跃的部分ID
    const activeSectionId = ref('synopsis');

    // 当前标题
    const currentTitle = computed(() => titleMap[activeSectionId.value] || '故事梗概与人物小传');

    const stripMarkdownSyntax = (text) => {
      if (typeof text !== 'string') return text == null ? '' : String(text);
      let output = text.replace(/\r\n/g, '\n');
      output = output.replace(/^\s{0,3}#{1,6}\s+/gm, '');
      output = output.replace(/^\s{0,3}>\s?/gm, '');
      output = output.replace(/!\[([^\]]*)\]\([^)]+\)/g, '$1');
      output = output.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
      output = output.replace(/^\s*[-*+•]\s+\[[ xX]\]\s+/gm, '');
      output = output.replace(/^\s*[-*+•]\s+/gm, '');
      output = output.replace(/^\s*\d+[.)]\s+/gm, '');
      output = output.replace(/`{3,}/g, '');
      output = output.replace(/(`+)(.*?)\1/g, '$2');
      output = output.replace(/(\*\*|__)(.*?)\1/g, '$2');
      output = output.replace(/(\*|_)(.*?)\1/g, '$2');
      output = output.replace(/~~(.*?)~~/g, '$1');
      output = output.replace(/【画面】/g, '△');
      output = output.replace(/^[-*_]{3,}\s*$/gm, '');
      output = output.replace(/[ \t]+$/gm, '');
      const lines = output.split('\n');
      output = lines
        .filter((line, index) => {
          const isEmptyLine = !line.trim();
          if (!isEmptyLine) return true;
          const prevLine = index > 0 ? lines[index - 1] : '';
          const nextLine = index < lines.length - 1 ? lines[index + 1] : '';
          return !prevLine.trim() || !nextLine.trim();
        })
        .join('\n');
      output = output
        .split('\n')
        .map((line, index) => {
          if (index === 0 || !line.trim()) return line;
          const content = line.replace(/^(?:\u3000\u3000| {2})+/g, '');
          return `　　${content}`;
        })
        .join('\n');
      return output;
    };

    const sanitizeEpisodeMap = (source) => {
      const result = {};
      Object.keys(source || {}).forEach((key) => {
        const value = source[key];
        result[key] = typeof value === 'string' ? stripMarkdownSyntax(value) : value;
      });
      return result;
    };

    const createEmptyEpisodeMap = () => {
      const result = {};
      episodeOptions.value.forEach((episode) => {
        result[episode] = '';
      });
      return result;
    };

    const parseEpisodeRange = (raw, fallbackEpisode) => {
      const fallback = Number.isFinite(fallbackEpisode) ? fallbackEpisode : 1;
      const text = typeof raw === 'string' ? raw.trim() : '';
      const rangeMatch = text.match(/(\d+)\s*-\s*(\d+)/);
      if (rangeMatch) {
        const start = parseInt(rangeMatch[1], 10);
        const end = parseInt(rangeMatch[2], 10);
        if (Number.isFinite(start) && Number.isFinite(end) && end >= start) {
          return { startEpisode: start, endEpisode: end, sylloge: `${start}-${end}` };
        }
      }
      const singleMatch = text.match(/^(\d+)$/);
      if (singleMatch) {
        const episode = parseInt(singleMatch[1], 10);
        if (Number.isFinite(episode)) {
          return { startEpisode: episode, endEpisode: episode, sylloge: `${episode}-${episode}` };
        }
      }
      return { startEpisode: fallback, endEpisode: fallback, sylloge: `${fallback}-${fallback}` };
    };

    // 进度条相关计算属性
    const showOutlineProgress = computed(() => {
      return totalEpisodesToGenerate.value > 0 && activeSectionId.value === 'outline';
    });

    const progressPercentage = computed(() => {
      if (totalEpisodesToGenerate.value === 0) return 0;

      // 重新生成时使用特殊的进度控制
      if (currentGenerationType.value === 'regenerate') {
        return regenerateProgress.value;
      }

      // 批量生成时使用原有逻辑
      return Math.round((completedEpisodes.value / totalEpisodesToGenerate.value) * 100);
    });

    const progressStatus = computed(() => {
      if (progressPercentage.value === 100) return 'success';
      if (progressPercentage.value > 0) return 'active';
      return '';
    });

    const progressTitle = computed(() => {
      if (currentGenerationType.value === 'outline') {
        return '正在生成';
      } else if (currentGenerationType.value === 'regenerate') {
        return '重新生成';
      }
      return '生成进度';
    });

    const progressText = computed(() => {
      if (currentGenerationType.value === 'regenerate') {
        // 重新生成单集时，显示具体的集数
        const episode = currentGeneratingEpisodeNumber.value || currentRegenerateEpisode.value;
        return `第 ${episode} 集`;
      } else {
        // 批量生成时，显示当前进度的集数范围
        const currentEpisode = currentGenerationStartEpisode.value + completedEpisodes.value;
        return `${currentEpisode}/${currentGenerationEndEpisode.value} 集`;
      }
    });

    // 导航处理函数
    const handleNavigate = (sectionId) => {
      // 如果正在初始化故事走向，阻止导航并显示提示
      if (storyBibleInitializing.value && (sectionId === 'outline' || sectionId === 'script')) {
        ElMessage.warning('正在初始化故事走向');
        return;
      }

      activeSectionId.value = sectionId;
      outlineEpisodeTexts.value = sanitizeEpisodeMap(outlineEpisodeTexts.value);
      scriptEpisodeTexts.value = sanitizeEpisodeMap(scriptEpisodeTexts.value);
      // 当切换到分集大纲时，初始化分集大纲数据
      if (sectionId === 'outline') {
        fetchOutlineData();
      }
      // 当切换到详细剧本时，初始化详细剧本数据
      if (sectionId === 'script') {
        fetchScriptData();
      }
    };

    // 获取剧本信息
    console.log(1);

    const fetchScriptInfo = async () => {
      const scriptId = route.query.id || currentScriptId.value;
      if (scriptId) {
        // 优先读取特定剧本的缓存信息，然后读取通用缓存信息
        const specificCachedInfo = localStorage.getItem(`scriptInfo_${scriptId}`);
        const generalCachedInfo = localStorage.getItem('scriptInfo');
        const cachedInfo = specificCachedInfo || generalCachedInfo;

        if (cachedInfo) {
          try {
            const parsedInfo = JSON.parse(cachedInfo);
            console.log(parsedInfo);
            scriptInfo.value = {
              title: parsedInfo.title || '',
              core_audience: parsedInfo.core_audience || '',
              writing_techniques: parsedInfo.writing_techniques || '',
              theme: parsedInfo.theme || '',
              script_description: parsedInfo.script_description || ''
            };
          } catch (error) {
            console.error('解析剧本信息失败:', error);
            // 如果解析失败，使用默认值
            scriptInfo.value = {
              title: '',
              core_audience: '',
              writing_techniques: '',
              theme: '',
              script_description: ''
            };
          }
        } else {
          // 如果没有缓存数据，使用默认值
          scriptInfo.value = {
            title: '',
            core_audience: '',
            writing_techniques: '',
            theme: '',
            script_description: ''
          };
        }

        // 调用接口获取剧本详情
        loading.value = true;
        try {
          const formData = new FormData();
          formData.append('script_id', scriptId);
          formData.append('type', 'synopsis');

          const response = await request.post('/playlet/query_details_of_the_script', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });

          console.log(response);
          if (response) {
            const cleanedSynopsis = stripMarkdownSyntax(response.story_outline_and_character_profiles || '');
            rawBaseText.value = cleanedSynopsis;
            baseText.value = cleanedSynopsis || null;
          }
        } catch (error) {
          console.error('获取剧本详情失败:', error);
        } finally {
          loading.value = false;
        }
      }
    };

    // 监听路由参数中的ID变化
    watch(() => route.query.id, (newId) => {
      if (newId) {
        activeSectionId.value = 'synopsis';
        currentScriptId.value = String(newId);
        localStorage.setItem('currentScriptId', currentScriptId.value);
        // 当路由参数变化时重新获取剧本信息
        fetchScriptInfo();

        // 如果是从创建剧本跳转过来，自动执行故事梗概生成
        if (route.query.action === 'create') {
          // 延迟执行，确保页面已完全加载
          nextTick(() => {
            setTimeout(() => {
              handleGenerateSynopsis();
            }, 500);
          });
        }
      }
    }, { immediate: true });

    // 注意：已在路由参数监听器中处理fetchScriptInfo调用，避免重复调用

    // 导航与滚动
    const mainContentRef = ref(null);

    // 获取分集大纲数据
    const fetchOutlineData = async () => {
      const scriptId = route.query.id || currentScriptId.value;
      if (scriptId) {
        // 显示加载状态
        outlineLoading.value = true;

        try {
          const formData = new FormData();
          formData.append('script_id', scriptId);

          const response = await request.post('/playlet/query_the_distribution_outline', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });

          console.log('分集大纲数据:', response);
          const payload = response && typeof response === 'object' && response.data && typeof response.data === 'object'
            ? response.data
            : response;
          if (payload) {
            const subsectionData = Array.isArray(payload.subsectionData) ? payload.subsectionData : [];
            const scriptContentData = Array.isArray(payload.script_content_data) ? payload.script_content_data : [];

            if (subsectionData.length > 0) {
              outlinePanels.value = subsectionData.map((item, index) => {
                const parsedRange = parseEpisodeRange(item?.number, index + 1);
                return {
                  id: index + 1,
                  inputText: stripMarkdownSyntax(item.text || ''),
                  sylloge: parsedRange.sylloge,
                  startEpisode: parsedRange.startEpisode,
                  endEpisode: parsedRange.endEpisode,
                  isSaved: true
                };
              });
              nextPanelId.value = subsectionData.length + 1;
            } else {
              outlinePanels.value = [{
                id: 1,
                inputText: '',
                sylloge: '1-10',
                startEpisode: 1,
                endEpisode: 10,
                isSaved: false
              }];
              nextPanelId.value = 2;
            }

            const nextOutlineEpisodeTexts = createEmptyEpisodeMap();
            scriptContentData.forEach((content, index) => {
              const episodeNumber = index + 1;
              if (episodeNumber in nextOutlineEpisodeTexts) {
                nextOutlineEpisodeTexts[episodeNumber] = stripMarkdownSyntax(content || '');
              }
            });
            outlineEpisodeTexts.value = sanitizeEpisodeMap(nextOutlineEpisodeTexts);
          }
        } catch (error) {
          console.error('获取分集大纲数据失败:', error);
        } finally {
          // 关闭加载状态
          outlineLoading.value = false;
        }
      }
    };

    // 获取详细剧本数据
    const fetchScriptData = async () => {
      const scriptId = route.query.id || currentScriptId.value;
      if (scriptId) {
        try {
          // 开启加载状态
          scriptLoading.value = true;

          const formData = new FormData();
          formData.append('script_id', scriptId);

          const response = await request.post('/playlet/query_verbatim_transcript', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });

          console.log('详细剧本数据:', response);
          const payload = response && typeof response === 'object' && response.data && typeof response.data === 'object'
            ? response.data
            : response;
          if (payload) {
            const schedulingOutlineArray = Array.isArray(payload.scheduling_outline_array) ? payload.scheduling_outline_array : [];
            const detailedScriptArray = Array.isArray(payload.detailed_script_array) ? payload.detailed_script_array : [];

            const nextOutlineEpisodeTexts = createEmptyEpisodeMap();
            schedulingOutlineArray.forEach((content, index) => {
              const episodeNumber = index + 1;
              if (episodeNumber in nextOutlineEpisodeTexts) {
                nextOutlineEpisodeTexts[episodeNumber] = stripMarkdownSyntax(content || '');
              }
            });
            outlineEpisodeTexts.value = sanitizeEpisodeMap(nextOutlineEpisodeTexts);

            const nextScriptEpisodeTexts = createEmptyEpisodeMap();
            detailedScriptArray.forEach((content, index) => {
              const episodeNumber = index + 1;
              if (episodeNumber in nextScriptEpisodeTexts) {
                nextScriptEpisodeTexts[episodeNumber] = stripMarkdownSyntax(content || '');
              }
            });
            scriptEpisodeTexts.value = sanitizeEpisodeMap(nextScriptEpisodeTexts);
          }
        } catch (error) {
          console.error('获取详细剧本数据失败:', error);
        } finally {
          // 关闭加载状态
          scriptLoading.value = false;
        }
      }
    };

    onMounted(() => {
      // 注意：fetchScriptInfo已在路由参数监听器中处理，无需在此重复调用
    });

    // 检查是否正在生成内容
    const isContentGenerating = computed(() => {
      return isGenerating.value ||
        batchGenerating.value ||
        generatingEpisodes.value.length > 0 ||
        generatingScriptEpisodes.value.length > 0 ||
        outlineLoading.value ||
        scriptLoading.value;
    });

    // 返回上一页
    const handleBack = async () => {
      try {
        // 检查是否正在生成内容
        if (isContentGenerating.value) {
          try {
            await ElMessageBox.confirm(
              '内容正在生成中，离开页面后有概率生成失败',
              '提示',
              {
                confirmButtonText: '确定离开',
                cancelButtonText: '取消',
                type: 'warning',
              }
            );
          } catch {
            // 用户点击取消，不执行返回操作
            return;
          }
        }

        // 执行返回操作
        if (window.history.length > 1) {
          router.back();
        } else {
          router.replace('/');
        }
      } catch (e) {
        router.replace('/');
      }
    };

    // 保存内容
    const handleSave = async () => {
      const activeSection = document.querySelector(`#${activeSectionId.value} .result-text`);
      if (activeSection) {
        const content = stripMarkdownSyntax(activeSection.innerHTML);

        try {
          // 显示加载状态
          loadingText.value = '保存中';
          loading.value = true;

          // 准备请求参数
          const formData = new FormData();
          formData.append('script_id', currentScriptId.value);

          // 根据页面类型设置type参数
          let type;
          if (activeSectionId.value === 'outline') {
            type = 'scheduling_outline';
          } else if (activeSectionId.value === 'script') {
            type = 'detailed_script';
          } else {
            type = activeSectionId.value; // synopsis等其他类型保持原样
          }

          formData.append('type', type);
          formData.append('content', content);

          // 调用保存接口
          const response = await request.post('/playlet/save_changes', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          // 检查响应
          if (response) {
            // 更新本地数据
            switch (activeSectionId.value) {
              case 'synopsis':
                rawBaseText.value = content;
                baseText.value = content;
                break;
              case 'outline':
                outlineText.value = content;
                break;
            }

            // 显示保存成功提示
            ElMessage.success('保存成功');
          } else {
            ElMessage.error('保存失败，请重试');
          }
        } catch (error) {
          console.error('保存失败:', error);
          ElMessage.error('保存失败，请重试');
        } finally {
          // 隐藏加载状态
          loading.value = false;
          loadingText.value = '正在加载中';
        }
      }
    };

    // 生成故事梗概和人物小传
    const handleGenerateSynopsis = async (retryCount = 0) => {
      const maxRetries = 3; // 最大重试次数

      try {
        loading.value = true;
        isGenerating.value = true;

        if (retryCount === 0) {
          loadingText.value = '正在生成故事梗概和人物小传';
          rawBaseText.value = '';
          baseText.value = '';
        } else {
          loadingText.value = '正在生成故事梗概和人物小传';
          if (!rawBaseText.value) {
            rawBaseText.value = baseText.value || '';
          }
        }

        // 调用流式接口
        const response = await request.streamRequest('/playlet/story_outline_creation', {
          script_id: currentScriptId.value
        })

        // 关闭加载状态，开始接收流式数据
        loading.value = false;

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let lastDataTime = Date.now(); // 记录最后接收数据的时间

        // 设置连接监控定时器
        const connectionMonitor = setInterval(() => {
          const now = Date.now();
          // 如果超过120秒没有接收到数据，认为连接可能断开
          if (now - lastDataTime > 1200000) {
            clearInterval(connectionMonitor);
            reader.cancel();
            throw new Error('连接超时，未接收到数据');
          }
        }, 10000);

        try {
          while (true) {
            const { done, value } = await reader.read();

            if (done) {
              clearInterval(connectionMonitor);
              break;
            }

            // 更新最后接收数据时间
            lastDataTime = Date.now();

            // 解码数据
            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;

            // 处理完整的行
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // 保留最后一个不完整的行

            for (const line of lines) {
              if (line.trim()) {
                // 处理SSE格式数据
                if (line.startsWith('data: ')) {
                  const data = line.substring(6);
                  if (data !== '[DONE]') {
                    try {
                      // 解析JSON数据并提取content字段
                      const jsonData = JSON.parse(data);
                      if (jsonData.content) {
                        await typewriterEffect(jsonData.content);
                      }
                    } catch (e) {
                      // 如果不是JSON格式，直接显示
                      await typewriterEffect(data);
                    }
                  }
                } else {
                  // 尝试解析JSON格式的普通文本
                  try {
                    const jsonData = JSON.parse(line);
                    if (jsonData.content) {
                      await typewriterEffect(jsonData.content);
                    }
                  } catch (e) {
                    // 如果不是JSON格式，直接显示
                    await typewriterEffect(line);
                  }
                }
              }
            }
          }
        } catch (streamError) {
          clearInterval(connectionMonitor);
          throw streamError;
        }

        ElMessage.success('故事梗概和人物小传生成成功');

        // 刷新用户数据
        await refreshUserData();

        // 故事梗概生成完成后，自动初始化故事走向（临时关闭，按需恢复）
        // await initializeStoryBible();
      } catch (error) {
        console.error('生成故事梗概和人物小传失败:', error);

        // 处理超时或网络错误，尝试重连
        if ((error.isTimeout || error.isNetworkError || error.message.includes('Failed to fetch')) && retryCount < maxRetries) {
          console.log(`连接断开，准备第 ${retryCount + 1} 次重试`);
          // 等待2秒后重试
          setTimeout(() => {
            handleGenerateSynopsis(retryCount + 1);
          }, 2000);
          return; // 不执行finally块，保持loading状态
        }

        // 达到最大重试次数或其他错误
        if (retryCount >= maxRetries) {
          ElMessage.error(`连接多次断开，请检查网络后重试`);
        } else {
          ElMessage.error('生成失败，请重试');
        }
      } finally {
        loading.value = false;
        isGenerating.value = false;
        loadingText.value = '正在加载中';
      }
    };

    // 打字机效果
    const typewriterEffect = async (text) => {
      rawBaseText.value += text;
      const sanitized = stripMarkdownSyntax(rawBaseText.value);
      const current = baseText.value || '';
      if (sanitized.length <= current.length) {
        baseText.value = sanitized;
        return;
      }
      const appendText = sanitized.slice(current.length);
      for (let i = 0; i < appendText.length; i++) {
        baseText.value += appendText[i];
        await new Promise(resolve => setTimeout(resolve, 20));
      }
    };

    // 刷新用户数据函数
    const refreshUserData = async () => {
      try {
        const response = await request.post('/user/refresh_user_data');

        if (response && Object.prototype.hasOwnProperty.call(response, 'balance_cny')) {
          const nextBalance = Number(response.balance_cny || 0);
          const userInfoRaw = localStorage.getItem('userInfo') || sessionStorage.getItem('userInfo');
          if (userInfoRaw) {
            const userInfo = JSON.parse(userInfoRaw);
            userInfo.balance_cny = nextBalance;
            if (localStorage.getItem('userInfo')) {
              localStorage.setItem('userInfo', JSON.stringify(userInfo));
            }
            if (sessionStorage.getItem('userInfo')) {
              sessionStorage.setItem('userInfo', JSON.stringify(userInfo));
            }
          }

          // 通知 Header 刷新余额
          window.dispatchEvent(new CustomEvent('balanceUpdated', {
            detail: { balance_cny: nextBalance }
          }));

          console.log('用户数据刷新成功，新余额:', nextBalance);
        }
      } catch (error) {
        console.error('刷新用户数据失败:', error);
      }
    };

    // 故事走向初始化函数
    const initializeStoryBible = async () => {
      try {
        storyBibleInitializing.value = true;

        // 显示初始化提示
        ElNotification({
          title: '故事走向初始化',
          message: '故事梗概已经生成完成，正在初始化故事走向，请耐心等待2~3分钟。请不要刷新页面。',
          type: 'info',
          duration: 8000
        });

        const formData = new FormData();
        formData.append('script_id', currentScriptId.value);

        const response = await request.post('/playlet/the_Story_Bible', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        console.log('故事走向初始化完成:', response);
        ElMessage.success('故事走向初始化完成');

        // 刷新用户数据
        await refreshUserData();
      } catch (error) {
        console.error('故事走向初始化失败:', error);
        ElMessage.error('故事走向初始化失败，请重试');
      } finally {
        storyBibleInitializing.value = false;
      }
    };

    // 面板管理方法
    const addOutlinePanel = () => {
      outlinePanels.value.push({
        id: nextPanelId.value,
        inputText: '',
        sylloge: '1-10',
        startEpisode: 1,
        endEpisode: 10,
        isSaved: false // 新添加的面板未保存
      });
      nextPanelId.value++;
      ElMessage.success('已添加新面板');
    };

    const removeOutlinePanel = async (panelId) => {
      if (outlinePanels.value.length <= 1) {
        ElMessage.warning('至少需要保留一个面板');
        return;
      }

      const index = outlinePanels.value.findIndex(panel => panel.id === panelId);
      if (index > -1) {
        const panel = outlinePanels.value[index];

        // 如果是新添加未保存的面板，直接删除，无需确认弹窗
        if (!panel.isSaved) {
          outlinePanels.value.splice(index, 1);
          ElMessage.success('已删除段落');
          return;
        }

        // 已保存的面板需要确认弹窗
        try {
          await ElMessageBox.confirm(
            '您即将删除这个分集大纲段落，删除后将无法恢复。\n\n确定要继续删除吗？',
            '⚠️ 删除段落确认',
            {
              type: 'warning',
              confirmButtonText: '确认删除',
              cancelButtonText: '取消',
              confirmButtonClass: 'el-button--danger',
              showCancelButton: true,
              closeOnClickModal: false,
              closeOnPressEscape: false,
              beforeClose: (action, instance, done) => {
                if (action === 'confirm') {
                  instance.confirmButtonLoading = true;
                  instance.confirmButtonText = '删除中...';
                  setTimeout(() => {
                    done();
                  }, 300);
                } else {
                  done();
                }
              }
            }
          );

          // 已保存的面板需要调用删除接口
          console.log('删除段落确认后的信息:');
          console.log('段落index:', index);
          console.log('剧本id:', currentScriptId.value);

          try {
            // 调用删除段落接口
            const formData = new FormData();
            formData.append('script_id', currentScriptId.value);
            formData.append('index', index.toString());

            const response = await request.post('/playlet/delete_paragraph', formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            });

            if (response) {
              // 接口调用成功，删除本地数据
              outlinePanels.value.splice(index, 1);
              ElMessage.success('已删除段落');
            } else {
              ElMessage.error('删除失败，请重试');
            }
          } catch (error) {
            console.error('删除段落接口调用失败:', error);
            ElMessage.error('删除失败，请重试');
          }
        } catch (e) {
          // 用户取消删除操作
          console.log('用户取消删除操作');
        }
      }
    };

    // 分集大纲处理方法
    const handleBeautifyOutline = (panelId) => {
      const panel = outlinePanels.value.find(p => p.id === panelId);
      if (!panel || !panel.inputText.trim()) {
        ElMessage.warning('请先输入要美化的内容');
        return;
      }
      // 这里可以添加美化逻辑
      ElMessage.success('美化功能开发中...');
    };





    const handleGenerateOutline = async (panelId) => {
      const panel = outlinePanels.value.find(p => p.id === panelId);
      if (!panel || !panel.inputText.trim()) {
        ElMessage.warning('请先输入生成内容的要求');
        return;
      }

      if (!panel.sylloge || !panel.sylloge.trim()) {
        ElMessage.warning('请设置分段集数范围');
        return;
      }

      // 解析sylloge参数，确定集数范围
      const syllogeMatch = panel.sylloge.match(/^(\d+)-(\d+)$/);
      if (!syllogeMatch) {
        ElMessage.warning('集数范围格式错误，请使用如"1-10"的格式');
        return;
      }

      const startEpisode = parseInt(syllogeMatch[1]);
      const endEpisode = parseInt(syllogeMatch[2]);

      if (startEpisode > endEpisode) {
        ElMessage.warning('起始集数不能大于结束集数');
        return;
      }

      // 设置生成状态
      isGenerating.value = true;
      loadingText.value = '内容生成中';

      // 初始化进度条
      const episodeCount = endEpisode - startEpisode + 1;
      totalEpisodesToGenerate.value = episodeCount;
      completedEpisodes.value = 0;
      currentGenerationType.value = 'outline';
      currentGenerationStartEpisode.value = startEpisode;
      currentGenerationEndEpisode.value = endEpisode;
      currentGeneratingEpisodeNumber.value = 0;

      // 保存原内容
      const originalContents = {};
      for (let i = startEpisode; i <= endEpisode; i++) {
        originalContents[i] = outlineEpisodeTexts.value[i] || '';
      }

      // 将整个集数范围添加到正在生成的数组中
      const episodeRange = [];
      for (let i = startEpisode; i <= endEpisode; i++) {
        episodeRange.push(i);
        outlineEpisodeTexts.value[i] = 'AI生成中...';
      }
      generatingEpisodes.value = episodeRange;
      // 强制触发响应式更新
      outlineEpisodeTexts.value = { ...outlineEpisodeTexts.value };

      try {
        // 按集数循环发起请求
        for (let episode = startEpisode; episode <= endEpisode; episode++) {
          try {
            // 更新当前正在生成的集数
            currentGeneratingEpisodeNumber.value = episode;
            loadingText.value = `正在生成第${episode}集内容...`;

            // 设置当前正在生成的集数
            currentGeneratingEpisode.value = episode;

            // 发起普通POST请求
            const response = await request.post('/playlet/scheduling_outline', {
              script_id: currentScriptId.value,
              importText: panel.inputText,
              number: episode,
              sylloge: panel.sylloge
            });

            // 清空"AI接入中"提示，设置返回的内容
            outlineEpisodeTexts.value[episode] = stripMarkdownSyntax(response || '');
            // 强制触发响应式更新
            outlineEpisodeTexts.value = { ...outlineEpisodeTexts.value };

            // 从正在生成的数组中移除当前集数
            const index = generatingEpisodes.value.indexOf(episode);
            if (index > -1) {
              generatingEpisodes.value.splice(index, 1);
            }

            // 更新进度
            completedEpisodes.value++;

            // 清空当前正在生成的集数状态
            currentGeneratingEpisode.value = null;
            ElMessage.success(`第${episode}集分集大纲生成成功`);
            // 刷新用户数据
            await refreshUserData();
          } catch (episodeError) {
            console.error(`生成第${episode}集时出错:`, episodeError);
            // ElMessage.error(`第${episode}集生成失败: ${episodeError.message || '请重试'}`);

            // 恢复当前集数的原内容
            outlineEpisodeTexts.value[episode] = originalContents[episode];

            // 恢复后续集数的原内容
            for (let i = episode + 1; i <= endEpisode; i++) {
              outlineEpisodeTexts.value[i] = originalContents[i];
            }

            // 强制触发响应式更新
            outlineEpisodeTexts.value = { ...outlineEpisodeTexts.value };

            // 停止后续集数的生成
            throw episodeError;
          }
        }

        // 生成成功后，将面板标记为已保存
        panel.isSaved = true;

      } catch (error) {
        console.error('生成分集大纲时出错:', error);
        // ElMessage.error(error.message || '生成失败，请重试');

        // 恢复原内容，不清空
        for (let i = startEpisode; i <= endEpisode; i++) {
          outlineEpisodeTexts.value[i] = originalContents[i];
        }
        // 强制触发响应式更新
        outlineEpisodeTexts.value = { ...outlineEpisodeTexts.value };
      } finally {
        isGenerating.value = false;
        loadingText.value = '正在加载中';
        currentGeneratingEpisode.value = null; // 确保状态重置
        generatingEpisodes.value = []; // 清空正在生成的集数数组

        // 重置进度条状态
        setTimeout(() => {
          totalEpisodesToGenerate.value = 0;
          completedEpisodes.value = 0;
          currentGenerationType.value = '';
          currentGenerationStartEpisode.value = 0;
          currentGenerationEndEpisode.value = 0;
          currentGeneratingEpisodeNumber.value = 0;
          regenerateProgress.value = 0;
        }, 2000); // 2秒后隐藏进度条，让用户能看到完成状态
      }
    };

    // 继续生成分集大纲（从未生成的集数开始）
    const handleContinueGenerate = async (panelId) => {
      const panel = outlinePanels.value.find(p => p.id === panelId);
      if (!panel || !panel.inputText.trim()) {
        ElMessage.warning('请先输入生成内容的要求');
        return;
      }

      if (!panel.sylloge || !panel.sylloge.trim()) {
        ElMessage.warning('请设置分段集数范围');
        return;
      }

      // 解析sylloge参数，确定集数范围
      const syllogeMatch = panel.sylloge.match(/^(\d+)-(\d+)$/);
      if (!syllogeMatch) {
        ElMessage.warning('集数范围格式错误，请使用如"1-10"的格式');
        return;
      }

      const startEpisode = parseInt(syllogeMatch[1]);
      const endEpisode = parseInt(syllogeMatch[2]);

      // 找到第一个未生成的集数作为继续生成的起点
      let continueStartEpisode = null;
      for (let i = startEpisode; i <= endEpisode; i++) {
        if (!outlineEpisodeTexts.value[i] || !outlineEpisodeTexts.value[i].trim() ||
          outlineEpisodeTexts.value[i] === 'AI生成中...') {
          continueStartEpisode = i;
          break;
        }
      }

      if (continueStartEpisode === null) {
        ElMessage.info('所有集数的分集大纲都已生成完成');
        return;
      }

      // 设置生成状态
      isGenerating.value = true;
      loadingText.value = '内容生成中';

      // 初始化进度条
      const episodeCount = endEpisode - continueStartEpisode + 1;
      totalEpisodesToGenerate.value = episodeCount;
      completedEpisodes.value = 0;
      currentGenerationType.value = 'outline';
      currentGenerationStartEpisode.value = continueStartEpisode;
      currentGenerationEndEpisode.value = endEpisode;
      currentGeneratingEpisodeNumber.value = 0;

      // 保存原内容（只保存未生成的部分）
      const originalContents = {};
      for (let i = continueStartEpisode; i <= endEpisode; i++) {
        originalContents[i] = outlineEpisodeTexts.value[i] || '';
      }

      // 将未生成的集数范围添加到正在生成的数组中
      const episodeRange = [];
      for (let i = continueStartEpisode; i <= endEpisode; i++) {
        episodeRange.push(i);
        outlineEpisodeTexts.value[i] = 'AI生成中...';
      }
      generatingEpisodes.value = episodeRange;
      // 强制触发响应式更新
      outlineEpisodeTexts.value = { ...outlineEpisodeTexts.value };

      try {
        // 按集数循环发起请求（从未生成的集数开始）
        for (let episode = continueStartEpisode; episode <= endEpisode; episode++) {
          try {
            // 更新当前正在生成的集数
            currentGeneratingEpisodeNumber.value = episode;
            loadingText.value = `正在生成第${episode}集内容...`;

            // 设置当前正在生成的集数
            currentGeneratingEpisode.value = episode;

            // 发起普通POST请求
            const response = await request.post('/playlet/scheduling_outline', {
              script_id: currentScriptId.value,
              importText: panel.inputText,
              number: episode,
              sylloge: panel.sylloge
            });

            // 清空"AI接入中"提示，设置返回的内容
            outlineEpisodeTexts.value[episode] = stripMarkdownSyntax(response || '');
            // 强制触发响应式更新
            outlineEpisodeTexts.value = { ...outlineEpisodeTexts.value };

            // 从正在生成的数组中移除当前集数
            const index = generatingEpisodes.value.indexOf(episode);
            if (index > -1) {
              generatingEpisodes.value.splice(index, 1);
            }

            // 更新进度
            completedEpisodes.value++;

            // 清空当前正在生成的集数状态
            currentGeneratingEpisode.value = null;
            ElMessage.success(`第${episode}集分集大纲生成成功`);
            // 刷新用户数据
            await refreshUserData();
          } catch (episodeError) {
            console.error(`生成第${episode}集时出错:`, episodeError);

            // 恢复当前集数的原内容
            outlineEpisodeTexts.value[episode] = originalContents[episode];

            // 恢复后续集数的原内容
            for (let i = episode + 1; i <= endEpisode; i++) {
              outlineEpisodeTexts.value[i] = originalContents[i];
            }

            // 强制触发响应式更新
            outlineEpisodeTexts.value = { ...outlineEpisodeTexts.value };

            // 停止后续集数的生成
            throw episodeError;
          }
        }

        // 生成成功后，将面板标记为已保存
        panel.isSaved = true;
        ElMessage.success(`继续生成完成，从第${continueStartEpisode}集到第${endEpisode}集`);

      } catch (error) {
        console.error('继续生成分集大纲时出错:', error);

        // 恢复原内容，不清空
        for (let i = continueStartEpisode; i <= endEpisode; i++) {
          outlineEpisodeTexts.value[i] = originalContents[i];
        }
        // 强制触发响应式更新
        outlineEpisodeTexts.value = { ...outlineEpisodeTexts.value };
      } finally {
        isGenerating.value = false;
        loadingText.value = '正在加载中';
        currentGeneratingEpisode.value = null; // 确保状态重置
        generatingEpisodes.value = []; // 清空正在生成的集数数组

        // 重置进度条状态
        setTimeout(() => {
          totalEpisodesToGenerate.value = 0;
          completedEpisodes.value = 0;
          currentGenerationType.value = '';
          currentGenerationStartEpisode.value = 0;
          currentGenerationEndEpisode.value = 0;
          currentGeneratingEpisodeNumber.value = 0;
          regenerateProgress.value = 0;
        }, 2000); // 2秒后隐藏进度条，让用户能看到完成状态
      }
    };

    // 保存单集内容
    const saveEpisodeContent = async (episode, contentType = 'outline') => {
      try {
        // 根据内容类型获取对应的数据
        const originalContent = contentType === 'script' ? scriptEpisodeTexts.value[episode] : outlineEpisodeTexts.value[episode];
        const content = stripMarkdownSyntax(originalContent || '');
        if (!content || !content.trim()) {
          ElMessage.warning('内容为空，无法保存');
          return;
        }
        if (contentType === 'script') {
          scriptEpisodeTexts.value[episode] = content;
        } else {
          outlineEpisodeTexts.value[episode] = content;
        }

        // 添加到保存状态数组
        savingEpisodes.value.push(episode);

        // 输出调试信息
        console.log('script_id:', currentScriptId.value);
        console.log('index:', episode);
        console.log('contentType:', contentType);
        console.log('当前对应的框里的文本:', content);

        // 这里可以添加保存到服务器的逻辑
        // await saveToServer(episode, content);
        // 准备请求参数
        const formData = new FormData();
        formData.append('script_id', currentScriptId.value);

        // 根据内容类型设置type参数
        const type = contentType === 'script' ? 'detailed_script' : 'scheduling_outline';
        formData.append('type', type);
        formData.append('content', content);
        formData.append('number', episode);
        // 调用保存接口
        const response = await request.post('/playlet/save_changes', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        // 检查响应
        if (response) {
          // 更新本地数据
          switch (activeSectionId.value) {
            case 'synopsis':
              baseText.value = content;
              break;
            case 'outline':
              outlineText.value = content;
              break;
          }
          // 显示保存成功提示
          ElMessage.success(`第${episode}集内容保存成功`);
        } else {
          ElMessage.error('第${episode}集保存失败，请重试');
        }


      } catch (error) {
        console.error('保存失败:', error);
        ElMessage.error('保存失败，请重试');
      } finally {
        // 从保存状态数组中移除
        const index = savingEpisodes.value.indexOf(episode);
        if (index > -1) {
          savingEpisodes.value.splice(index, 1);
        }
      }
    };

    const handleExportScriptDocx = async () => {
      const availableEpisodes = episodeOptions.value.filter((episode) => hasScriptContent.value(episode));
      if (!availableEpisodes.length) {
        ElMessage.warning('暂无可导出的详细剧本内容');
        return;
      }

      try {
        const children = [
          new Paragraph({
            text: `${scriptInfo.value.title || '未命名项目'} - 详细剧本`,
            heading: HeadingLevel.TITLE,
          }),
          new Paragraph({ text: '' }),
        ];

        availableEpisodes.forEach((episode) => {
          const rawContent = scriptEpisodeTexts.value[episode] || '';
          const content = stripMarkdownSyntax(rawContent);
          const lines = content.split(/\r?\n/);

          children.push(
            new Paragraph({
              text: `第${episode}集`,
              heading: HeadingLevel.HEADING_1,
            })
          );

          if (!lines.length || !content.trim()) {
            children.push(new Paragraph({ text: '（暂无内容）' }));
          } else {
            lines.forEach((line) => {
              children.push(
                new Paragraph({
                  children: [new TextRun(line || ' ')],
                })
              );
            });
          }

          children.push(new Paragraph({ text: '' }));
        });

        const doc = new Document({
          sections: [{ children }],
        });
        const blob = await Packer.toBlob(doc);
        const fileName = `${scriptInfo.value.title || '详细剧本'}_${new Date().toISOString().slice(0, 10)}.docx`;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        ElMessage.success('详细剧本导出成功');
      } catch (error) {
        console.error('导出详细剧本失败:', error);
        ElMessage.error('导出失败，请稍后重试');
      }
    };

    // 清空单集内容
    const clearEpisodeContent = async (episode) => {
      console.log("重新生成");
      console.log("点击的集数编号:", episode);

      // 设置当前要重新生成的集数并显示对话框
      regenerateDialogMode.value = 'outline';
      currentRegenerateEpisode.value = episode;
      regenerateInputText.value = ''; // 清空输入框
      regenerateDialogVisible.value = true; // 显示对话框

      return;

      // 找到包含该集数的面板
      const targetPanel = outlinePanels.value.find(panel => {
        const syllogeMatch = panel.sylloge.match(/^(\d+)-(\d+)$/);
        if (syllogeMatch) {
          const startEpisode = parseInt(syllogeMatch[1]);
          const endEpisode = parseInt(syllogeMatch[2]);
          return episode >= startEpisode && episode <= endEpisode;
        }
        return false;
      });

      if (!targetPanel) {
        ElMessage.warning('未找到对应的面板数据');
        return;
      }

      if (!targetPanel.inputText.trim()) {
        ElMessage.warning('请先输入生成内容的要求');
        return;
      }

      // 保存原内容
      const originalContent = outlineEpisodeTexts.value[episode] || '';

      // 设置该集数为生成中状态
      generatingEpisodes.value.push(episode);
      outlineEpisodeTexts.value[episode] = 'AI生成中...';

      try {
        // 调用API生成单集内容
        const response = await request.post('/playlet/scheduling_outline', {
          script_id: currentScriptId.value,
          importText: targetPanel.inputText,
          number: episode,
          sylloge: targetPanel.sylloge
        });

        // 设置返回的内容
        outlineEpisodeTexts.value[episode] = stripMarkdownSyntax(response || '');

        // 刷新用户数据
        await request.post('/user/refresh_user_data');

      } catch (error) {
        console.error('生成失败:', error);
        // 恢复原内容，不清空
        outlineEpisodeTexts.value[episode] = originalContent;
      } finally {
        // 移除生成中状态
        const index = generatingEpisodes.value.indexOf(episode);
        if (index > -1) {
          generatingEpisodes.value.splice(index, 1);
        }
      }
    };

    // 生成详细剧本内容
    const generateScriptContent = async (episode) => {
      // 保存原内容
      const originalContent = scriptEpisodeTexts.value[episode] || '';

      try {
        // 检查是否有对应的分集大纲内容
        const outlineContent = outlineEpisodeTexts.value[episode];
        console.log('点击生成时获取到的数据:', {
          episode: episode,
          outlineContent: outlineContent,
          scriptId: currentScriptId.value,
          hasContent: !!(outlineContent && outlineContent.trim())
        });

        if (!outlineContent || !outlineContent.trim()) {
          ElMessage.warning(`没有第${episode}集的分集大纲，请先生成分集大纲内容`);
          return;
        }

        if (generatingScriptEpisodes.value.includes(episode)) {
          return;
        }

        // 添加到生成状态数组
        generatingScriptEpisodes.value.push(episode);
        scriptEpisodeTexts.value[episode] = 'AI生成中...';

        let minute = episode * 2
        // 显示通知提示
        ElNotification({
          title: '生成提示',
          message: `生成${episode}集的内容需要${minute}分钟，请不要刷新页面，避免生成失败。`,
          type: 'info',
          duration: 5000
        });

        // 发起请求生成详细剧本
        const formData = new FormData();
        formData.append('script_id', currentScriptId.value);
        formData.append('number', episode);
        formData.append('text', outlineContent);

        const response = await request.post('/playlet/generate_script', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        // 设置返回的内容
        scriptEpisodeTexts.value[episode] = stripMarkdownSyntax(response || '');
        ElMessage.success(`第${episode}集详细剧本生成完成`);

        // 刷新用户数据
        const index = generatingScriptEpisodes.value.indexOf(episode);
        if (index > -1) {
          generatingScriptEpisodes.value.splice(index, 1);
        }
        refreshUserData();
      } catch (error) {
        console.error('生成详细剧本时出错:', error);
        // ElMessage.error(error.message || '生成失败，请重试');
        // 恢复原内容，不清空
        scriptEpisodeTexts.value[episode] = originalContent;
      } finally {
        // 从生成状态数组中移除
        let index = generatingScriptEpisodes.value.indexOf(episode);
        while (index > -1) {
          generatingScriptEpisodes.value.splice(index, 1);
          index = generatingScriptEpisodes.value.indexOf(episode);
        }
      }
    };

    // 重新生成详细剧本内容
    const regenerateScriptContent = async (episode) => {
      regenerateDialogMode.value = 'script';
      currentRegenerateEpisode.value = episode;
      regenerateInputText.value = '';
      regenerateDialogVisible.value = true;
    };

    // 批量生成详细剧本内容
    const handleBatchGenerate = async () => {
      if (!batchStartEpisode.value || !batchEndEpisode.value) {
        ElMessage.warning('请选择集数范围');
        return;
      }

      if (batchStartEpisode.value > batchEndEpisode.value) {
        ElMessage.warning('起始集数不能大于结束集数');
        return;
      }

      try {
        batchGenerating.value = true;

        // 收集需要生成的集数（不在此处检查分集大纲）
        const episodesToGenerate = [];
        for (let episode = batchStartEpisode.value; episode <= batchEndEpisode.value; episode++) {
          episodesToGenerate.push(episode);
        }

        // 生成前先整段校验：范围内每一集都必须有分集大纲
        const missingOutlineEpisodes = episodesToGenerate.filter((episode) => {
          const outlineContent = outlineEpisodeTexts.value[episode];
          return !outlineContent || !outlineContent.trim();
        });
        if (missingOutlineEpisodes.length > 0) {
          ElMessage.warning(`第${missingOutlineEpisodes.join('、')}集缺少分集大纲，请先补全后再批量生成`);
          return;
        }

        // 显示批量生成通知提示
        const totalEpisodes = episodesToGenerate.length;
        ElNotification({
          title: '批量生成提示',
          message: `生成${totalEpisodes}集的内容需要${totalEpisodes * 2}分钟，请不要刷新页面，避免生成失败。`,
          type: 'info',
          duration: 8000
        });

        // 串行生成：一集完成后再生成下一集
        const results = [];

        for (const episode of episodesToGenerate) {
          const originalContent = scriptEpisodeTexts.value[episode] || '';
          if (!generatingScriptEpisodes.value.includes(episode)) {
            generatingScriptEpisodes.value.push(episode);
          }
          scriptEpisodeTexts.value[episode] = 'AI生成中...';

          try {
            const outlineContent = outlineEpisodeTexts.value[episode];

            const formData = new FormData();
            formData.append('script_id', currentScriptId.value);
            formData.append('number', episode);
            formData.append('text', outlineContent);

            const response = await request.post('/playlet/generate_script', formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            });

            scriptEpisodeTexts.value[episode] = stripMarkdownSyntax(response || '');
            refreshUserData();
            results.push({ episode, success: true });
          } catch (error) {
            console.error(`生成第${episode}集详细剧本时出错:`, error);
            scriptEpisodeTexts.value[episode] = originalContent;
            results.push({ episode, success: false, error });
            break;
          } finally {
            const index = generatingScriptEpisodes.value.indexOf(episode);
            if (index > -1) {
              generatingScriptEpisodes.value.splice(index, 1);
            }
          }
        }

        // 注意：加载状态已在每个请求完成时立即清理，无需在此处重复清理

        // 统计结果
        const successCount = results.filter(r => r.success).length;
        const skippedCount = results.filter(r => r.skipped).length;
        const failCount = results.filter(r => !r.success && !r.skipped).length;

        if (failCount === 0 && skippedCount === 0) {
          ElMessage.success(`批量生成完成，共生成${successCount}集`);
        } else if (failCount === 0 && skippedCount > 0) {
          ElMessage.success(`批量生成完成，成功${successCount}集，跳过${skippedCount}集（无分集大纲）`);
        }
      } catch (error) {
        console.error('批量生成时出错:', error);
        ElMessage.error('批量生成失败，请重试');
      } finally {
        batchGenerating.value = false;
      }
    };

    // 计算字数
    const getWordCount = (text) => {
      if (!text) return 0;
      return text.replace(/\s/g, '').length;
    };

    // 监听分集大纲面板的集数范围变化，自动更新sylloge字段
    watch(outlinePanels, (newPanels) => {
      newPanels.forEach(panel => {
        if (panel.startEpisode && panel.endEpisode) {
          panel.sylloge = `${panel.startEpisode}-${panel.endEpisode}`;
        }
      });
    }, { deep: true });

    // 计算是否有故事梗概数据（作为分集大纲和详细剧本的前置条件）
    const hasOutlineData = computed(() => {
      return !!(baseText.value && baseText.value.trim());
    });

    // 对话框处理方法
    const handleRegenerateCancel = () => {
      regenerateDialogVisible.value = false;
      regenerateInputText.value = '';
      currentRegenerateEpisode.value = null;
      regenerateDialogMode.value = 'outline';
    };

    const handleRegenerateConfirm = async () => {
      if (!regenerateInputText.value.trim()) {
        ElMessage.warning('请输入重新生成的要求或描述');
        return;
      }

      const episode = currentRegenerateEpisode.value;
      const regenerateText = regenerateInputText.value.trim();

      if (regenerateDialogMode.value === 'script') {
        const outlineContent = outlineEpisodeTexts.value[episode];
        if (!outlineContent || !outlineContent.trim()) {
          ElMessage.warning(`没有第${episode}集的分集大纲，请先生成分集大纲内容`);
          return;
        }

        regenerateDialogVisible.value = false;

        if (generatingScriptEpisodes.value.includes(episode)) {
          return;
        }

        const originalContent = scriptEpisodeTexts.value[episode] || '';
        generatingScriptEpisodes.value.push(episode);
        scriptEpisodeTexts.value[episode] = 'AI生成中...';

        try {
          const formData = new FormData();
          formData.append('script_id', currentScriptId.value);
          formData.append('number', episode);
          formData.append('text', `${outlineContent}\n\n补充要求：${regenerateText}`);
          formData.append('describe', regenerateText);

          const response = await request.post('/playlet/generate_script', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });

          scriptEpisodeTexts.value[episode] = stripMarkdownSyntax(response || '');
          ElMessage.success(`第${episode}集详细剧本重新生成成功`);
          refreshUserData();
        } catch (error) {
          console.error('详细剧本重新生成失败:', error);
          scriptEpisodeTexts.value[episode] = originalContent;
          ElMessage.error(`第${episode}集详细剧本重新生成失败`);
        } finally {
          let index = generatingScriptEpisodes.value.indexOf(episode);
          while (index > -1) {
            generatingScriptEpisodes.value.splice(index, 1);
            index = generatingScriptEpisodes.value.indexOf(episode);
          }
          regenerateInputText.value = '';
          currentRegenerateEpisode.value = null;
          regenerateDialogMode.value = 'outline';
        }
        return;
      }

      // 找到包含该集数的面板
      const targetPanel = outlinePanels.value.find(panel => {
        const syllogeMatch = panel.sylloge.match(/^(\d+)-(\d+)$/);
        if (syllogeMatch) {
          const startEpisode = parseInt(syllogeMatch[1]);
          const endEpisode = parseInt(syllogeMatch[2]);
          return episode >= startEpisode && episode <= endEpisode;
        }
        return false;
      });

      if (!targetPanel) {
        ElMessage.warning('未找到对应的面板数据');
        return;
      }

      // 关闭对话框
      regenerateDialogVisible.value = false;

      // 初始化进度条状态（重新生成单集）
      totalEpisodesToGenerate.value = 1;
      completedEpisodes.value = 0;
      currentGenerationStartEpisode.value = targetPanel.episode;
      currentGenerationEndEpisode.value = targetPanel.episode;
      currentGeneratingEpisodeNumber.value = targetPanel.episode;
      currentGenerationType.value = 'regenerate';
      regenerateProgress.value = 0;

      // 启动进度动画，缓慢增长到90%
      const progressInterval = setInterval(() => {
        if (regenerateProgress.value < 90) {
          regenerateProgress.value += Math.random() * 3 + 1; // 每次增加1-4%
          if (regenerateProgress.value > 90) {
            regenerateProgress.value = 90;
          }
        }
      }, 200); // 每200ms更新一次

      // 添加到生成中状态
      if (!generatingEpisodes.value.includes(episode)) {
        generatingEpisodes.value.push(episode);
      }

      // 保存原内容
      const originalContent = outlineEpisodeTexts.value[episode] || '';

      // 设置生成中状态
      outlineEpisodeTexts.value[episode] = 'AI生成中...';

      try {
        // 调用API生成单集内容，添加describe参数
        const response = await request.post('/playlet/scheduling_outline', {
          script_id: currentScriptId.value,
          importText: targetPanel.inputText,
          number: episode,
          sylloge: targetPanel.sylloge,
          describe: regenerateText
        });

        // 清除进度动画
        clearInterval(progressInterval);

        // 直接跳到100%
        regenerateProgress.value = 100;

        // 设置返回的内容
        outlineEpisodeTexts.value[episode] = stripMarkdownSyntax(response || '');

        // 更新进度
        completedEpisodes.value++;

        // 立即隐藏进度条
        setTimeout(() => {
          totalEpisodesToGenerate.value = 0;
          completedEpisodes.value = 0;
          currentGenerationType.value = '';
          currentGenerationStartEpisode.value = 0;
          currentGenerationEndEpisode.value = 0;
          currentGeneratingEpisodeNumber.value = 0;
          regenerateProgress.value = 0;
        }, 1000); // 1秒后隐藏进度条，让用户能看到完成状态

        ElMessage.success(`第${episode}集分集大纲重新生成成功`);

        // 刷新用户数据（在后台执行，不影响进度条显示）
        request.post('/user/refresh_user_data').catch(error => {
          console.error('刷新用户数据失败:', error);
        });

      } catch (error) {
        // 清除进度动画
        clearInterval(progressInterval);

        console.error('重新生成失败:', error);
        ElMessage.error(`第${episode}集分集大纲重新生成失败`);
        // 恢复原内容
        outlineEpisodeTexts.value[episode] = originalContent;

        // 错误时立即重置进度条状态
        totalEpisodesToGenerate.value = 0;
        completedEpisodes.value = 0;
        currentGenerationType.value = '';
        currentGenerationStartEpisode.value = 0;
        currentGenerationEndEpisode.value = 0;
        currentGeneratingEpisodeNumber.value = 0;
        regenerateProgress.value = 0;
      } finally {
        // 移除生成中状态
        const index = generatingEpisodes.value.indexOf(episode);
        if (index > -1) {
          generatingEpisodes.value.splice(index, 1);
        }

        // 清空对话框状态
        regenerateInputText.value = '';
        currentRegenerateEpisode.value = null;
        regenerateDialogMode.value = 'outline';
      }
    };

    // 聊天相关方法
    const handleSendMessage = () => {
      if (!chatInputText.value.trim()) return;

      // 确保 chatMessages.value 是数组
      if (!Array.isArray(chatMessages.value)) {
        chatMessages.value = [];
      }

      // 添加用户消息
      const userMessage = {
        type: 'user',
        content: chatInputText.value.trim(),
        time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
      };
      chatMessages.value.push(userMessage);

      // 清空输入框
      const messageContent = chatInputText.value.trim();
      chatInputText.value = '';

      // 模拟AI回复（这里可以后续接入真实的AI接口）
      setTimeout(() => {
        const aiMessage = {
          type: 'assistant',
          content: `作为大纲助手，我可以帮您分析剧情结构、优化人物设定、完善故事逻辑等。请告诉我您具体需要什么帮助？`,
          time: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
        };
        chatMessages.value.push(aiMessage);

        // 滚动到底部
        nextTick(() => {
          const chatMessagesEl = document.querySelector('.chat-messages');
          if (chatMessagesEl) {
            chatMessagesEl.scrollTop = chatMessagesEl.scrollHeight;
          }
        });
      }, 1000);

      // 滚动到底部
      nextTick(() => {
        const chatMessagesEl = document.querySelector('.chat-messages');
        if (chatMessagesEl) {
          chatMessagesEl.scrollTop = chatMessagesEl.scrollHeight;
        }
      });
    };

    // 拖拽处理方法
    const handleMouseDown = (event) => {
      isDragging.value = true;
      dragStartX.value = event.clientX;
      dragStartWidth.value = leftPanelWidth.value;

      // 添加全局鼠标事件监听
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      // 防止文本选择
      event.preventDefault();
    };

    const handleMouseMove = (event) => {
      if (!isDragging.value) return;

      const deltaX = event.clientX - dragStartX.value;
      const newWidth = dragStartWidth.value + deltaX;

      // 限制最小和最大宽度
      const minWidth = 200;
      const maxWidth = 500;

      if (newWidth >= minWidth && newWidth <= maxWidth) {
        leftPanelWidth.value = newWidth;
      }
    };

    const handleMouseUp = () => {
      isDragging.value = false;

      // 移除全局鼠标事件监听
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    // 初始化
    onMounted(() => {
      initEpisodeTexts();
      outlineEpisodeTexts.value = sanitizeEpisodeMap(outlineEpisodeTexts.value);
      scriptEpisodeTexts.value = sanitizeEpisodeMap(scriptEpisodeTexts.value);
      baseText.value = stripMarkdownSyntax(baseText.value || '');
    });

    onActivated(async () => {
      const scriptId = route.query.id || currentScriptId.value;
      if (!scriptId) return;
      await fetchScriptInfo();
      if (activeSectionId.value === 'outline') {
        await fetchOutlineData();
      } else if (activeSectionId.value === 'script') {
        await fetchDetailedScriptData();
      }
    });

    return {
      // refs
      mainContentRef,
      activeSectionId,
      currentScriptId,
      // script info
      scriptInfo,
      // contents
      baseText,
      outlineText,
      loading,
      loadingText,
      outlineLoading,
      isGenerating,
      currentGeneratingEpisode,
      generatingEpisodes,
      savingEpisodes,
      // outline related
      outlineInputText,
      selectedEpisode,
      episodeTexts,
      episodeOptions,
      outlinePanels,
      nextPanelId,
      // outline related
      outlineEpisodeTexts,
      // script related
      scriptEpisodeTexts,
      generatingScriptEpisodes,
      scriptLoading,
      // batch generate related
      batchStartEpisode,
      batchEndEpisode,
      batchGenerating,
      storyBibleInitializing,
      // computed
      currentTitle,
      hasOutlineData,
      isContentGenerating,
      // 进度条相关
      showOutlineProgress,
      progressPercentage,
      progressStatus,
      progressTitle,
      progressText,
      totalEpisodesToGenerate,
      completedEpisodes,
      currentGenerationType,
      regenerateProgress,
      // methods
      handleNavigate,
      handleBack,
      handleSave,
      handleGenerateSynopsis,
      handleBeautifyOutline,
      handleGenerateOutline,
      handleContinueGenerate,
      shouldShowContinueGenerate,
      getNextEpisodeToGenerate,
      getGeneratedEpisodeCount,
      hasInputText,
      hasEpisodeContent,
      hasScriptContent,
      hasAnyScriptContent,
      addOutlinePanel,
      removeOutlinePanel,
      initEpisodeTexts,
      saveEpisodeContent,
      clearEpisodeContent,
      generateScriptContent,
      regenerateScriptContent,
      handleExportScriptDocx,
      handleBatchGenerate,
      getWordCount,
      fetchOutlineData,
      // 对话框相关
      regenerateDialogVisible,
      regenerateInputText,
      currentRegenerateEpisode,
      regenerateDialogMode,
      handleRegenerateCancel,
      handleRegenerateConfirm,
      // 聊天相关
      chatMessages,
      chatInputText,
      handleSendMessage,
      // 拖拽相关
      leftPanelWidth,
      isDragging,
      handleMouseDown,
      ArrowLeft,
      Plus,
      Close,
    };
  }
};
</script>

<style scoped>
/* 分集大纲布局样式 */
.outline-layout {
  display: flex;
  min-height: calc(100vh - 200px);
  /* 改为最小高度，允许内容撑开 */
  gap: 16px;
  flex-wrap: wrap;
  /* 允许换行 */
}

.outline-left-container {
  width: 40%;
  min-width: 320px;
  /* 设置最小宽度 */
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 8px;
  height: calc(100vh - 222px);
  /* 设置固定高度以启用独立滚动 */
  overflow-y: auto;
  /* 启用垂直滚动 */
}

.add-panel-section {
  flex-shrink: 0;
  padding: 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  border: 1px solid rgba(226, 232, 240, 0.8);
}

.add-panel-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%) !important;
  border: none !important;
  border-radius: 10px !important;
  font-weight: 600 !important;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3) !important;
  transition: all 0.3s ease !important;
}

.add-panel-btn:hover {
  background: linear-gradient(135deg, #059669 0%, #047857 100%) !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4) !important;
}

.outline-panels-list {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
  /* 移除max-height和overflow-y，让父容器处理滚动 */
}

.outline-left-panel {
  flex-shrink: 0;
  min-height: 300px;
  /* 改为最小高度，允许内容撑开 */
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(226, 232, 240, 0.8);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  /* 恢复为hidden，保持面板边界 */
  position: relative;
}

.outline-left-panel:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  border-color: rgba(99, 102, 241, 0.3);
}

.outline-left-panel::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #f59e0b 0%, #f97316 50%, #ef4444 100%);
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px 12px;
  background: linear-gradient(135deg, #fafbff 0%, #f0f4ff 100%);
  border-bottom: 1px solid rgba(226, 232, 240, 0.6);
  margin: 0;
}

.panel-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 8px;
}

.panel-title::before {
  content: '✏️';
  font-size: 14px;
}

.outline-input-area {
  flex: 1;
  padding: 11px 10px;
  min-height: 0;
  /* 确保flex子项可以收缩 */
}

.outline-input-area .el-textarea {
  height: 100%;
}

.outline-input-area .el-textarea__inner {
  height: 240px !important;
  resize: none;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 17px;
  line-height: 2;
  background: #fafbfc;
  transition: all 0.3s ease;
  padding: 16px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.outline-input-area .el-textarea__inner:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  background: #ffffff;
  outline: none;
}

.outline-input-area .el-textarea__inner::placeholder {
  color: #94a3b8;
  font-style: italic;
}

.outline-control-area {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
  padding: 12px 20px 16px;
  background: #f8fafc;
  border-top: 1px solid rgba(226, 232, 240, 0.6);
  flex-wrap: wrap;
}

.episode-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.episode-selector label {
  font-size: 13px;
  font-weight: 500;
  color: #606266;
  white-space: nowrap;
}

.episode-range {
  display: flex;
  align-items: center;
  gap: 6px;
}

.episode-select {
  width: 80px;
}

.range-separator {
  font-size: 12px;
  color: #909399;
  font-weight: 500;
}

.outline-buttons {
  display: flex;
}

.outline-buttons .el-button {
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.outline-buttons .el-button:not(.el-button--primary) {
  background: #ffffff;
  border-color: #e2e8f0;
  color: #64748b;
}

.outline-buttons .el-button:not(.el-button--primary):hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
  color: #475569;
  transform: translateY(-1px);
}

.outline-buttons .el-button--primary {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border: none;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
}

.outline-buttons .el-button--primary:hover {
  background: linear-gradient(135deg, #5855eb 0%, #7c3aed 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
}

.outline-right-panel {
  width: 60%;
  min-width: 400px;
  /* 设置最小宽度 */
  padding-left: 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 0 12px 12px 0;
  overflow-y: auto;
  /* 添加独立滚动 */
  height: calc(100vh - 200px);
  /* 设置固定高度以启用独立滚动 */
  flex: 1;
  /* 允许弹性伸缩 */
}

.episode-textboxes {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  padding: 8px;
}

.episode-card {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(226, 232, 240, 0.8);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  position: relative;
}

.episode-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  border-color: rgba(99, 102, 241, 0.3);
}

.episode-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 50%, #06b6d4 100%);
}




.episode-content {
  padding: 16px 20px;
}

.episode-textarea {
  font-size: 17px;
}

.episode-textarea .el-textarea__inner {
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 17px;
  line-height: 2;
  resize: none;
  background: #fafbfc;
  transition: all 0.3s ease;
  padding: 18px 16px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.episode-textarea .el-textarea__inner:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  background: #ffffff;
  outline: none;
}

.episode-textarea .el-textarea__inner::placeholder {
  color: #94a3b8;
  font-style: italic;
}

.outline-input-area :deep(.el-textarea__inner),
.episode-textarea :deep(.el-textarea__inner) {
  line-height: 2 !important;
  padding-top: 18px !important;
  padding-bottom: 18px !important;
}

.episode-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px 16px;
  background: #f8fafc;
  border-top: 1px solid rgba(226, 232, 240, 0.6);
}

.word-count {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.word-count::before {
  content: '📊';
  font-size: 10px;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.action-buttons .el-button {
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
  padding: 6px 16px;
}

.action-buttons .el-button:not(.el-button--primary) {
  background: #ffffff;
  border-color: #e2e8f0;
  color: #64748b;
}

.action-buttons .el-button:not(.el-button--primary):hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
  color: #475569;
  transform: translateY(-1px);
}

.action-buttons .el-button--primary {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border: none;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
}

.action-buttons .el-button--primary:hover {
  background: linear-gradient(135deg, #5855eb 0%, #7c3aed 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
}

.sylloge-input-area {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  border-top: 1px solid #f0f0f0;
  margin-top: 8px;
}

.sylloge-input-area label {
  font-size: 13px;
  color: #606266;
  white-space: nowrap;
}

.loading-indicator {
  padding: 0 16px;
}

.result-view-layout {
  display: flex;
  gap: 16px;
  height: 100vh;
  overflow: hidden;
  box-sizing: border-box;
}

.left-info-panel {
  min-width: 200px;
  max-width: 500px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  flex-shrink: 0;
  position: relative;
  transition: width 0.1s ease-out;
}

.info-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 16px;
  text-align: center;
}

.info-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.info-content {
  padding: 16px;
  height: calc(100vh - 120px - 60px);
  overflow-y: auto;
}

.info-item {
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.info-item:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.info-item label {
  display: block;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
  font-size: 14px;
}

.info-item span {
  color: #666;
  font-size: 14px;
  line-height: 1.5;
}

.info-item.description .description-text {
  color: #666;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 305px;
  overflow-y: auto;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
}

.result-main-content {
  flex: 1;
  height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
}

.fixed-header {
  padding: 0px 16px 5px 16px;
  background: #fff;
  flex-shrink: 0;
}

.header-row {
  display: flex;
  align-items: center;
  padding-top: 2px;
  justify-content: space-between;
  gap: 20px;
}

.back-btn,
.save-btn,
.export-btn {
  flex-shrink: 0;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.back-btn:hover,
.save-btn:hover,
.export-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.scrollable-content {
  flex: 1;
  overflow: auto;
  padding-top: 16px;
  box-sizing: border-box;
}

.content-section {
  width: 100%;
  height: auto;
}

#synopsis.content-section {
  min-height: 100%;
}

.result-title,
.character-title,
.outline-title,
.transcript-title {
  font-size: 20px;
  font-weight: 600;
  margin: 12px 0;
}

.result-title {
  font-size: 24px;
  line-height: 1.3;
  margin: 0;
  letter-spacing: 0.5px;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 2px 12px rgba(90, 103, 216, 0.25);
  font-weight: 600;
  flex: 1;
  text-align: center;
}

.result-text,
.character-text,
.outline-text,
.transcript-text {
  white-space: pre-wrap;
  word-wrap: break-word;
  word-break: break-all;
  overflow-x: hidden;
  overflow-y: auto;
  max-height: 70vh;
  font-size: 16px;
  line-height: 1.9;
  margin: 8px 0;
  padding: 0px 10px;
  outline: none;
  box-sizing: border-box;
}

#synopsis .result-text {
  min-height: 100%;
  max-height: none;
  overflow-y: visible;
  margin: 0;
  padding: 0 10px 16px;
}

.generate-btn,
.outline-btn,
.transcript-btn {
  margin: 16px 0;
}

.bottom-gap {
  height: 24px;
}

.generate-button-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
}

.generate-button {
  padding: 12px 24px;
  font-size: 16px;
  background-color: #409EFF;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.generate-button:hover {
  background-color: #337ecc;
}

.generate-button:active {
  background-color: #2b6cb0;
}

.generate-button:disabled {
  background-color: #c0c4cc;
  cursor: not-allowed;
  opacity: 0.6;
}

.generate-button:disabled:hover {
  background-color: #c0c4cc;
}

/* 旧样式已被新的卡片设计替换 */

/* 详细剧本布局样式 */
.script-layout {
  display: flex;
  min-height: calc(100vh - 218px);
  gap: 16px;
  flex-wrap: wrap;
  /* 允许换行 */
}

.script-left-container {
  width: 50%;
  min-width: 350px;
  /* 设置最小宽度 */
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 8px;
  height: 100%;
  overflow-y: auto;
  flex: 1;
  /* 允许弹性伸缩 */
}

.script-right-container {
  width: 50%;
  min-width: 350px;
  /* 设置最小宽度 */
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 8px;
  height: 100%;
  overflow-y: auto;
  flex: 1;
  /* 允许弹性伸缩 */
}

.script-panel {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 100%;
}

.loading-container {
  width: 100%;
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

/* 剧本页面控件样式优化 */
.script-controls {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-left: 20px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #f8f9ff 0%, #f0f2ff 100%);
  border-radius: 12px;
  border: 1px solid #e1e6ff;
  box-shadow: 0 2px 8px rgba(90, 103, 216, 0.08);
}

.episode-range-selector {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 0;
}

.episode-range-selector label {
  font-size: 14px;
  font-weight: 500;
  color: #4a5568;
  white-space: nowrap;
  margin-right: 4px;
}

.episode-select {
  width: 90px;
}

.episode-select .el-input__inner {
  border-radius: 8px;
  border: 1px solid #d1d9e6;
  transition: all 0.3s ease;
}

.episode-select .el-input__inner:focus {
  border-color: #5a67d8;
  box-shadow: 0 0 0 2px rgba(90, 103, 216, 0.1);
}

.range-separator {
  font-size: 14px;
  color: #718096;
  font-weight: 500;
  padding: 0 4px;
}

.script-controls .el-button {
  padding: 8px 16px;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(90, 103, 216, 0.15);
}

.script-controls .el-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(90, 103, 216, 0.2);
}

.script-controls .el-button:disabled {
  transform: none;
  box-shadow: none;
  opacity: 0.6;
}

.batch-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: 16px;
}

.batch-controls .el-select {
  width: 80px;
}

.batch-controls .batch-text {
  font-size: 14px;
  color: #606266;
  white-space: nowrap;
}

.batch-controls .el-button {
  margin-left: 8px;
}

@media (max-width: 1024px) {

  .outline-layout {
    flex-direction: column;
    min-height: auto;
  }

  .outline-left-container,
  .outline-right-panel {
    width: 100%;
    min-width: auto;
    margin-bottom: 20px;
    height: auto;
  }

  .outline-right-panel {
    padding-left: 8px;
    border-radius: 12px;
  }
}

@media (max-width: 768px) {

  .outline-layout {
    gap: 8px;
    padding: 8px;
  }

  .generate-button-container {
    height: 120px;
    margin-top: 16px;
  }

  .generate-button {
    padding: 12px 24px;
    font-size: 16px;
  }

  .outline-left-container {
    padding: 4px;
    gap: 16px;
    height: auto;
  }

  .outline-right-panel {
    padding: 8px;
    height: auto;
  }

  .outline-left-panel {
    min-height: auto;
    overflow-y: auto;
  }

  .outline-input-area .el-textarea__inner {
    height: 120px !important;
  }

  .outline-control-area {
    flex-wrap: wrap;
    justify-content: flex-start;
    gap: 8px;
    padding: 8px 12px 12px;
  }

  .episode-selector {
    flex: 1 0 150px;
    justify-content: center;
    flex-wrap: wrap;
    gap: 4px;
  }

  .episode-selector label {
    white-space: normal;
    text-align: center;
  }

  .episode-range {
    flex-wrap: wrap;
    justify-content: center;
    gap: 4px;
  }

  .episode-select {
    width: auto;
    min-width: 60px;
  }

  .outline-buttons {
    flex: 1 0 150px;
    justify-content: center;
    flex-wrap: wrap;
    gap: 6px;
  }

  .outline-buttons .el-button {
    padding: 4px 12px;
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .generate-button-container {
    height: 100px;
    margin-top: 12px;
  }

  .generate-button {
    padding: 10px 20px;
    font-size: 14px;
  }

  .outline-layout {
    gap: 4px;
    padding: 4px;
  }

  .outline-left-panel {
    min-height: auto;
    overflow-y: auto;
  }

  .outline-input-area .el-textarea__inner {
    height: 80px !important;
  }

  .outline-control-area {
    flex-wrap: wrap;
    justify-content: flex-start;
    gap: 6px;
    padding: 6px 8px 8px;
  }

  .episode-selector {
    flex: 1 0 120px;
    justify-content: center;
    flex-wrap: wrap;
    gap: 4px;
  }

  .episode-selector label {
    white-space: normal;
    text-align: center;
    font-size: 12px;
  }

  .episode-range {
    flex-wrap: wrap;
    justify-content: center;
    gap: 2px;
  }

  .episode-select {
    width: auto;
    min-width: 50px;
  }

  .outline-buttons {
    flex: 1 0 120px;
    justify-content: center;
    flex-wrap: wrap;
    gap: 4px;
  }

  .outline-buttons .el-button {
    padding: 3px 10px;
    font-size: 11px;
  }
}

.regenerate-dialog-content {
  padding: 10px 0;
}

.dialog-description {
  margin-bottom: 15px;
  color: #606266;
  font-size: 14px;
  line-height: 1.5;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.resize-handle {
  position: absolute;
  top: 0;
  right: 0;
  width: 8px;
  height: 100%;
  background: transparent;
  cursor: col-resize;
  z-index: 10;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.resize-handle:hover {
  background: #409eff;
  opacity: 0.6;
}

.resize-handle.dragging {
  background: #409eff;
  opacity: 0.8;
}

.resize-icon {
  font-size: 16px;
  color: #000;
  opacity: 0.6;
  transition: all 0.2s ease;
  font-weight: bold;
  user-select: none;
  font-family: monospace;
  line-height: 1;
  position: relative;
  z-index: 9999;
  left: -5px;
  top: 20px;
}

.resize-handle:hover .resize-icon {
  opacity: 0.8;
}

.resize-handle.dragging .resize-icon {
  opacity: 1;
}

/* 拖拽时禁用文本选择 */
.result-view-layout.dragging {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

/* 故事梗概与人物小传样式 */
.synopsis-content {
  height: 100%;
}

.synopsis-preview-text {
  margin: 0;
  padding: 4px 2px 0;
  max-height: none;
  height: 100%;
  overflow-y: auto;
}

/* 聊天对话框样式 */
.chat-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f5f5f5;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #ddd;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  min-height: 0;
  background-color: #f5f5f5;
}

.chat-messages::-webkit-scrollbar {
  width: 6px;
}

.chat-messages::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.chat-messages::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 消息行布局 */
.message-row {
  display: flex;
  margin-bottom: 16px;
  align-items: flex-start;
}

.message-left {
  justify-content: flex-start;
}

.message-right {
  justify-content: flex-end;
}

/* 头像样式 */
.message-avatar {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
}

.avatar-ai,
.avatar-user {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  color: white;
}

.avatar-ai {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.avatar-ai-img {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  object-fit: cover;
  display: block;
}

.avatar-user {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

/* 消息内容包装器 */
.message-content-wrapper {
  max-width: calc(100% - 60px);
  margin: 0 10px;
}

.message-right .message-content-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.message-left .message-content-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

/* 气泡样式 */
.message-bubble {
  padding: 10px 16px;
  border-radius: 18px;
  font-size: 14px;
  line-height: 1.4;
  word-wrap: break-word;
  position: relative;
  max-width: 100%;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.bubble-ai {
  background: #ffffff;
  color: #333;
  border-radius: 12px;
}

.bubble-user {
  background: #95ec69;
  color: #333;
  border-radius: 12px;
}



/* 无消息提示 */
.no-messages {
  text-align: center;
  color: #999;
  font-size: 14px;
  margin-top: 40px;
}

.chat-input-area {
  background: white;
}

.chat-input-container {
  border-top: 1px solid #ddd;
  padding: 16px;
  display: flex;
  flex-direction: column;
  background: #fff;
}

.chat-input-wrapper {
  width: 100%;
}

.chat-input-wrapper :deep(.el-textarea__inner) {
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
}

.chat-input-wrapper .el-textarea {
  width: 100%;
}

.chat-input-wrapper .el-textarea .el-textarea__inner {
  resize: none;
  border: 0;
  padding: 0;
  min-height: 80px;
  max-height: 150px;
  font-size: 14px;
  line-height: 1.5;
  box-shadow: none;
}

.chat-input-wrapper .el-textarea .el-textarea__inner:focus {
  border: none;
  box-shadow: none;
  outline: none;
}

.chat-button-wrapper {
  display: flex;
  justify-content: flex-end;
  padding-top: 4px;
}

.chat-send-button {
  border-radius: 6px;
  width: 60px !important;
  height: 32px !important;
  padding: 0;
  background: #007bff;
  border-color: #007bff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.chat-send-button:hover {
  background: #0056b3;
  border-color: #0056b3;
}

.empty-chat {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  font-size: 14px;
}

.empty-chat-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.no-synopsis {
  color: #999;
  font-style: italic;
  text-align: center;
  padding: 20px;
}

/* 固定进度条样式 */
.fixed-progress-container {
  display: flex;
  align-items: center;
  font-size: 24px;
  line-height: 1.3;
  margin: 0;
  letter-spacing: 0.5px;
  font-weight: 600;
  flex: 1;
  text-align: center;
  gap: 12px;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.progress-title {
  font-weight: 600;
  color: #333;
  font-size: 14px;
}

.progress-text {
  color: #666;
  font-size: 13px;
}
</style>
